import { z as zod } from "zod"
import type { FormSections } from "connected-components/optimize-experiments/configuration-sections"
import { createPercentSchema } from "./base"
import { isBlankValue } from "./helpers"

interface FieldValidatorApi {
  form: {
    state: {
      values: FormSections
    }
  }
}

const rulesPercentInputSchema = createPercentSchema({ min: 0 })

export const maxTurnoverSchema = createPercentSchema({ min: 0 }).nullable()

export const concentrationSchema = zod.object({
  minConcentration: createPercentSchema({ min: 0, max: 100 }),
  maxConcentration: createPercentSchema({ min: 0, max: 100 }),
}).refine(
  (data) => {
    if (data == null) return true
    const minEmpty = isBlankValue(data.minConcentration)
    const maxEmpty = isBlankValue(data.maxConcentration)
    if (minEmpty || maxEmpty) return true

    const minNum = parseFloat(data.minConcentration)
    const maxNum = parseFloat(data.maxConcentration)
    if (isNaN(minNum) || isNaN(maxNum)) return true

    return minNum <= maxNum
  },
  { message: "Min concentration must be less than or equal to max concentration" },
).nullable()

export const maxTradeSchema = zod.object({
  percentADV: rulesPercentInputSchema,
  percentEquity: rulesPercentInputSchema,
  percentOriginalEconomicExposure: rulesPercentInputSchema,
}).refine(
  (data) => {
    if (data == null) return true
    const hasADV = data.percentADV !== "" && data.percentADV != null && !isNaN(parseFloat(data.percentADV))
    const hasEquity = data.percentEquity !== "" && data.percentEquity != null && !isNaN(parseFloat(data.percentEquity))
    const hasRelative = data.percentOriginalEconomicExposure !== "" && data.percentOriginalEconomicExposure != null && !isNaN(parseFloat(data.percentOriginalEconomicExposure))
    return hasADV || hasEquity || hasRelative
  },
  { message: "At least one value must be entered" },
).nullable()

export const equityChangeSchema = createPercentSchema().nullable()
export const netLongSchema = zod.object({
  longMarketValue: createPercentSchema({ min: 0, max: 100 }),
  shortMarketValue: zod.number(),
}).nullable()

function parseNumericValue(value: string | number | undefined | null): number | undefined {
  if (value === null || value === undefined || value === "") return undefined
  const numValue = typeof value === "string" ? parseFloat(value) : value
  return isNaN(numValue) ? undefined : numValue
}

const minMaxSchema = zod.object({
  min: zod.number(),
  max: zod.number(),
}).refine(
  (data) => data.min <= data.max,
  { message: "Max value must be greater than or equal to min value" },
)

function createMinMaxValidator(
  ruleDisplayType: "exposure" | "composition",
  index: number,
  field: "min" | "max",
) {
  return ({ value, fieldApi }: { value: string | number | undefined | null, fieldApi: FieldValidatorApi }) => {
    const currentValue = parseNumericValue(value)
    if (currentValue === undefined) return undefined

    const formState = fieldApi.form.state.values
    const rules = formState?.rules?.[ruleDisplayType]
    const oppositeField = field === "min" ? "max" : "min"
    const oppositeValue = parseNumericValue(rules?.[index]?.[oppositeField])

    if (oppositeValue === undefined) return undefined

    const result = minMaxSchema.safeParse({
      min: field === "min" ? currentValue : oppositeValue,
      max: field === "max" ? currentValue : oppositeValue,
    })

    return result.success ? undefined : result.error.issues[0].message
  }
}

export function factorRuleMinValidator(ruleDisplayType: "exposure" | "composition", index: number) {
  return createMinMaxValidator(ruleDisplayType, index, "min")
}

export function factorRuleMaxValidator(ruleDisplayType: "exposure" | "composition", index: number) {
  return createMinMaxValidator(ruleDisplayType, index, "max")
}
